import { ApiStatus, ContractOrderType, ContractServices, OrderDirection, TriggerMode, TriggerType } from 'network'
import { useDoubleContractKlineStore } from './use-double-contract-kline'
import { useDoubleContractAssetsAccountStore } from './use-double-contract-assets-account'
import { useExchangeInfoStore } from './use-exchange-info'
import { useI18n } from 'i18n-utils'
import { useProjectTheme } from './use-project-theme'
import { useAssetsStore } from './use-assets'
import { useUserInfoStore } from './use-user-info'
import { requestIdleCallbackFrame } from 'utils'

export const useDoubleContractOrderListStore = defineStore('doubleContractOrderListStore', () => {
  const limitOrders = ref<ApiResponse.DoubleContractLimitOrder[]>([])
  const klineStore = useDoubleContractKlineStore()
  const doubleContractAssetsAccountStore = useDoubleContractAssetsAccountStore()
  const icebergStrategyOrders = ref<ApiResponse.DoubleContractIcebergStrategyOrder[]>([])
  const exchangeInfoStore = useExchangeInfoStore()
  const assetsStore = useAssetsStore()
  const currentHideOtherOrder = ref(false)
  const strategyHideOtherOrder = ref(false)
  const userInfoStore = useUserInfoStore()

  const { t } = useI18n()
  const themeStore = useProjectTheme()

  const timeoutLimitOrders = ref<NodeJS.Timeout | null>(null)
  const timeoutIcebergStrategyOrders = ref<NodeJS.Timeout | null>(null)

  const stopTimeoutIcebergStrategyOrders = () => {
    if (timeoutIcebergStrategyOrders.value) {
      clearTimeout(timeoutIcebergStrategyOrders.value)
    }
  }

  const stopTimeoutLimitOrders = () => {
    if (timeoutLimitOrders.value) {
      clearTimeout(timeoutLimitOrders.value)
    }
  }

  const getLimitOrders = async (isForce = false) => {
    if (!userInfoStore.isLogin) return []
    if (doubleContractAssetsAccountStore.doubleContractAssetsAccount?.openFutures !== 1 && !isForce) return []
    stopTimeoutLimitOrders()
    const res = await ContractServices.getLimitOrders()
    if (res.status === ApiStatus.Success) {
      limitOrders.value = res.data

      timeoutLimitOrders.value = setTimeout(() => {
        requestIdleCallbackFrame(() => {
          getLimitOrders()
        })
      }, 3000)
    }
  }

  const getIcebergStrategyOrders = async (isForce = false) => {
    if (!userInfoStore.isLogin) return []
    if (doubleContractAssetsAccountStore.doubleContractAssetsAccount?.openFutures !== 1 && !isForce) return []

    stopTimeoutIcebergStrategyOrders()
    const res = await ContractServices.getIcebergStrategyOrders({ page: 1, pageSize: 10000 })
    if (res.status === ApiStatus.Success) {
      icebergStrategyOrders.value = res.data.list
      timeoutIcebergStrategyOrders.value = setTimeout(() => {
        requestIdleCallbackFrame(() => {
          getIcebergStrategyOrders()
        })
      }, 3000)
    }
  }

  const refreshLimitOrders = async () => {
    assetsStore.refresh()
    getLimitOrders()
  }

  const currentLimitOrders = computed(() => {
    if (!klineStore.currentSymbolId) return []
    return limitOrders.value.filter((item) => item.symbolId === klineStore.currentSymbolId)
  })

  const currentPositions = computed(() => {
    if (!klineStore.currentSymbolId) return []
    return doubleContractAssetsAccountStore.positions.filter((item) => item.symbolId === klineStore.currentSymbolId)
  })

  const getCloseOrOpenTag = (data: ApiResponse.DoubleContractLimitOrder) => {
    const { positionType, type, direction } = data

    if (type === ContractOrderType.Limit) {
      if (positionType === OrderDirection.Buy) {
        return direction === OrderDirection.Buy ? '开多' : '平多'
      }
      return direction === OrderDirection.Sell ? '开空' : '平空'
    }

    if (positionType === OrderDirection.Buy) {
      return '平多'
    }
    return '平空'
  }

  const getStatus = (data: ApiResponse.DoubleContractLimitOrder) => {
    if (data.type === ContractOrderType.Limit) {
      return ''
    }
    const statusMap = {
      0: '未生效',
      1: '未触发',
    }
    return statusMap[data.status as keyof typeof statusMap] || ''
  }

  const refreshIcebergStrategyOrders = async () => {
    assetsStore.refresh()
    getIcebergStrategyOrders()
  }

  const getTriggerCondition = (data: ApiResponse.DoubleContractLimitOrder) => {
    if (data.type === ContractOrderType.Limit) {
      return ''
    }
    const { positionType, type } = data
    if (positionType === OrderDirection.Buy) {
      if (type === ContractOrderType.MarketStopLoss) return '<='
      return '>='
    }
    if (type === ContractOrderType.MarketStopLoss) return '>='
    return '<='
  }

  const getOrderType = (data: ApiResponse.DoubleContractLimitOrder) => {
    const { type } = data
    const isLimit = type === ContractOrderType.Limit
    if (isLimit) return ''
    if (type === ContractOrderType.MarketStopLoss) return '止损'
    if (type === ContractOrderType.MarketTakeProfit) return '止盈'
    return ''
  }

  const getOpenInterest = (data: ApiResponse.DoubleContractLimitOrder) => {
    const symbolInfo = exchangeInfoStore.getContractSymbolInfoBySymbolId(data.symbolId)
    return data.qty.multiplyStr(symbolInfo?.contractValue || 1).removeTrailingZero
  }

  const getQty = (data: ApiResponse.DoubleContractLimitOrder) => {
    const symbolInfo = exchangeInfoStore.getContractSymbolInfoBySymbolId(data.symbolId)
    if (data.type === ContractOrderType.Limit) {
      const dealAmount = data.qty.subtractStr(data.restQty).multiplyStr(symbolInfo!.contractValue).multiplyStr(data.price)
      const amount = data.qty.multiplyStr(symbolInfo?.contractValue).multiplyStr(data.price)
      return `${dealAmount.formatCommonMoney()}/${amount.formatCommonMoney()} USDT`
    }
    if (data.qty === -1) return t('全部')
    const openInterest = getOpenInterest(data)
    return openInterest.multiplyStr(data.triggerPrice).formatCommonMoney() + 'USDT'
  }

  /** 获取策略状态 */
  const getStrategyStatus = (entity: ApiResponse.DoubleContractIcebergStrategyOrder) => {
    let orderStatus = ''
    let orderStatusColor = ''
    switch (entity!.status) {
      case 1:
        orderStatus = t('等待信号')
        orderStatusColor = themeStore.theme.colors.warning
        break
      case 2:
        orderStatus = t('运行中')
        orderStatusColor = themeStore.theme.colors.success
        break
      case 3:
        orderStatus = t('已停止')
        orderStatusColor = themeStore.theme.colors.text.base
        break
      case 4:
        orderStatus = t('已失败')
        orderStatusColor = themeStore.theme.colors.text.base
        break
      default:
        return { orderStatus: '', orderStatusColor: '' }
    }
    return { orderStatus, orderStatusColor }
  }

  /** 获取冰山委托OrderType */
  const getIcebergStrategyOrderType = (data: ApiResponse.DoubleContractIcebergStrategyOrder) => {
    if (data.positionType === OrderDirection.Buy) {
      return data.direction === OrderDirection.Buy ? '开多' : '平多'
    }
    return data.direction === OrderDirection.Sell ? '开空' : '平空'
  }

  /** 获取冰山委托委托总量 */
  const getIcebergStrategyTotalQty = (data: ApiResponse.DoubleContractIcebergStrategyOrder) => {
    const config = exchangeInfoStore.getContractSymbolInfoBySymbolId(data.symbolId)
    return data.totalQty.multiplyStr(config?.contractValue).removeTrailingZero
  }

  /** 获取冰山委托已成交量 */
  const getIcebergStrategyAlreadyQty = (data: ApiResponse.DoubleContractIcebergStrategyOrder) => {
    const config = exchangeInfoStore.getContractSymbolInfoBySymbolId(data.symbolId)
    return data.alreadyQty.multiplyStr(config?.contractValue).removeTrailingZero
  }

  /** 获取冰山委托单笔委托量 */
  const getIcebergStrategySingleQty = (data: ApiResponse.DoubleContractIcebergStrategyOrder) => {
    const config = exchangeInfoStore.getContractSymbolInfoBySymbolId(data.symbolId)
    return data.singleQty.multiplyStr(config?.contractValue).removeTrailingZero
  }

  /** 获取冰山委托触发条件 */
  const getIcebergStrategyTriggerCondition = (data: ApiResponse.DoubleContractIcebergStrategyOrder) => {
    if (data.triggerMode !== TriggerMode.Immediate) {
      const temp = data.triggerType === TriggerType.Mark ? t('标记价格') : t('最新价格')
      return `${temp} | ${data.triggerPrice.formatCommonMoney()}`
    }
    return t('立即触发')
  }

  return {
    getLimitOrders,
    refreshLimitOrders,
    limitOrders,
    currentLimitOrders,
    currentPositions,
    getCloseOrOpenTag,
    getStatus,
    getIcebergStrategyOrders,
    refreshIcebergStrategyOrders,
    icebergStrategyOrders,
    getTriggerCondition,
    getOrderType,
    getOpenInterest,
    getQty,
    getStrategyStatus,
    getIcebergStrategyOrderType,
    getIcebergStrategyTotalQty,
    getIcebergStrategyAlreadyQty,
    getIcebergStrategySingleQty,
    getIcebergStrategyTriggerCondition,
    currentHideOtherOrder,
    strategyHideOtherOrder,
    stopTimeoutLimitOrders,
    stopTimeoutIcebergStrategyOrders,
  }
})
